import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { SIMPLE_DATE_REGEX } from '../../../constants';

@Component({
  selector: 'phoenix-date-range',
  templateUrl: './date-range.component.html',
  standalone: false,
})
export class DateRangeComponent implements OnInit, OnDestroy {
  @Input() startDateControl = new FormControl('');
  @Input() endDateControl = new FormControl('');
  start!: string;
  end!: string;
  today!: string;

  @Input() isRow: boolean = true;
  @Input() startDatePlaceholder: string = 'Start Date';
  @Input() endDatePlaceholder: string = 'End Date';
  @Input() idPrefix: string = 'datetime-input';

  @Output() startDate = new EventEmitter<string>();
  @Output() endDate = new EventEmitter<string>();
  @Input() initToYesterday = false;

  private destroy$ = new Subject();

  ngOnInit(): void {
    this.startDateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(_start => {
        if (_start) {
          this.start = SIMPLE_DATE_REGEX.test(_start)
            ? `${_start}T00:00Z`
            : `${_start}Z`;
          this.startDate.emit(this.start);
          this.validateStartDate(this.start);
        } else {
          this.start = '';
          this.startDate.emit('');
        }
        if (this.end) {
          this.validateEndDate(this.start, this.end);
        } else {
          this.endDateControl.setErrors(null, { emitEvent: true });
        }
      });

    this.endDateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(_end => {
        if (_end) {
          this.end = SIMPLE_DATE_REGEX.test(_end)
            ? `${_end}T00:00Z`
            : `${_end}Z`;
          this.endDate.emit(this.end);
          this.validateEndDate(this.start, this.end);
        } else {
          this.end = '';
          this.endDate.emit('');
        }
      });

    setTimeout(() => {
      if (this.initToYesterday) {
        this.getDefaultInitDate();
      }

      if (!this.today) {
        this.today = moment().utc().format('YYYY-MM-DDT00:00:00');
      }
    });
  }

  validateStartDate(date: string): void {
    const today = moment().utc().format('YYYY-MM-DDT00:00:00');
    const formattedDate = moment(date, moment.ISO_8601, true).format();
    if (moment(formattedDate, moment.ISO_8601, true).isValid()) {
      if (moment(formattedDate).isAfter(today)) {
        this.startDateControl.setErrors(
          { invalidFutureDate: true },
          { emitEvent: true }
        );
      }
    }
  }

  validateEndDate(startDate: string, endDate: string): void {
    if (!startDate || !endDate) {
      this.endDateControl.setErrors(null, { emitEvent: true });
      return;
    }

    const formattedStartDate = moment(
      startDate,
      moment.ISO_8601,
      true
    ).format();
    const formattedEndDate = moment(endDate, moment.ISO_8601, true).format();
    if (
      moment(formattedStartDate, moment.ISO_8601, true).isValid() &&
      moment(formattedEndDate, moment.ISO_8601, true).isValid()
    ) {
      if (moment(formattedEndDate).isBefore(formattedStartDate)) {
        this.endDateControl.setErrors(
          { invalidEndDate: true },
          { emitEvent: true }
        );
      } else {
        this.endDateControl.setErrors(null, { emitEvent: true });
      }
    }
  }

  private getDefaultInitDate(): void {
    const date = moment()
      .utc()
      .subtract(1, 'days')
      .format('YYYY-MM-DDT00:00:00');
    this.startDateControl.setValue(date);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
